import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Download, ExternalLink, Trash2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { ColorBlock, BigStat } from "@/components/page";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DOMAINS,
  KIND_LABEL,
  OPEN_QUESTIONS,
  SHIR_DERECH_YEAR,
  domainAverage,
  downloadCsv,
  fmtAvg,
  fmtDateTime,
  overallAverage,
  publicFormPath,
  type QuestionnaireKind,
  type ShirDerechResponse,
} from "@/lib/shirDerech";

function useResponses() {
  const api = useApi();
  return useQuery({
    queryKey: ["shir-derech-responses", SHIR_DERECH_YEAR],
    queryFn: async (): Promise<ShirDerechResponse[]> => {
      const res = await api(`/api/shir-derech/responses?year=${encodeURIComponent(SHIR_DERECH_YEAR)}`);
      if (!res.ok) throw new Error(`GET /api/shir-derech/responses → ${res.status}`);
      return (await res.json()) as ShirDerechResponse[];
    },
    staleTime: 15_000,
  });
}

function csvName(suffix: string) {
  return `shir-derech-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
}

export function ShirDerechHomePage() {
  const { data, isLoading } = useResponses();
  const rows = data ?? [];
  const opening = rows.filter((r) => r.kind === "opening");
  const closing = rows.filter((r) => r.kind === "closing");

  return (
    <div className="space-y-6 sm:space-y-7" dir="rtl">
      <header className="pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
          שיר דרך · שנת {SHIR_DERECH_YEAR}
        </p>
        <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
          שאלוני פתיחה וסיום.
        </h1>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
          שולחים למשתתפים את הקישור לשאלון, והתשובות מגיעות לכאן.
        </p>
      </header>

      <ColorBlock tint="lilac" size="md">
        <div className="grid grid-cols-2 gap-6 sm:gap-10">
          <BigStat label="מילאו שאלון פתיחה" value={isLoading ? "…" : opening.length} size="lg" />
          <BigStat label="מילאו שאלון סיום" value={isLoading ? "…" : closing.length} size="lg" />
        </div>
      </ColorBlock>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormLinkCard kind="opening" />
        <FormLinkCard kind="closing" />
        <ResponsesLinkCard kind="opening" count={isLoading ? null : opening.length} />
        <ResponsesLinkCard kind="closing" count={isLoading ? null : closing.length} />
      </div>

      <div className="flex justify-start">
        <button
          type="button"
          disabled={rows.length === 0}
          onClick={() => downloadCsv(sortForCsv(rows), csvName("all"))}
          className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          הורדת כל התשובות (CSV)
        </button>
      </div>
    </div>
  );
}

function sortForCsv(rows: ShirDerechResponse[]) {
  return rows
    .slice()
    .sort((a, b) =>
      a.kind === b.kind ? b.created_at.localeCompare(a.created_at) : a.kind === "opening" ? -1 : 1,
    );
}

function FormLinkCard({ kind }: { kind: QuestionnaireKind }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${publicFormPath(kind)}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("העתק את הקישור:", url);
    }
  }
  return (
    <div className="rounded-[24px] bg-white p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">קישור למשתתפים</p>
      <h2 className="mt-1 text-lg font-bold text-stone-900">שאלון {KIND_LABEL[kind]}</h2>
      <p className="mt-2 break-all text-xs text-stone-500" dir="ltr">
        {url}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "הועתק" : "העתקת קישור"}
        </button>
        <a
          href={publicFormPath(kind)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          פתיחת השאלון
        </a>
      </div>
    </div>
  );
}

function ResponsesLinkCard({ kind, count }: { kind: QuestionnaireKind; count: number | null }) {
  return (
    <Link
      to={`/shir-derech/${kind}`}
      className="group rounded-[24px] bg-white p-6 transition-colors hover:bg-stone-50"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">תשובות</p>
      <div className="mt-1 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-stone-900">תשובות לשאלון {KIND_LABEL[kind]}</h2>
        <ArrowLeft className="h-4 w-4 text-stone-400 transition-transform group-hover:-translate-x-1" />
      </div>
      <p className="mt-2 text-sm text-stone-600">{count === null ? "טוען…" : `${count} משתתפים`}</p>
    </Link>
  );
}

export function ShirDerechResponsesPage({ kind }: { kind: QuestionnaireKind }) {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading, error } = useResponses();
  const rows = (data ?? []).filter((r) => r.kind === kind);
  const [selected, setSelected] = useState<ShirDerechResponse | null>(null);

  const del = useMutation({
    mutationFn: async (id: number) => {
      const res = await api(`/api/shir-derech/responses/${id}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) throw new Error(`DELETE → ${res.status}`);
    },
    onSuccess: () => {
      setSelected(null);
      qc.invalidateQueries({ queryKey: ["shir-derech-responses"] });
    },
    onError: (err) => alert(`שגיאה במחיקה: ${err instanceof Error ? err.message : String(err)}`),
  });

  return (
    <div className="space-y-6" dir="rtl">
      <header className="pb-2">
        <Link to="/shir-derech" className="text-sm text-stone-500 hover:text-stone-800">
          → שיר דרך {SHIR_DERECH_YEAR}
        </Link>
        <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
          תשובות לשאלון {KIND_LABEL[kind]}
        </h1>
        <p className="mt-2 text-sm text-stone-600">לחיצה על כרטיס פותחת את כל התשובות של המשתתף.</p>
      </header>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-stone-600">{isLoading ? "טוען…" : `${rows.length} משתתפים`}</p>
        <button
          type="button"
          disabled={rows.length === 0}
          onClick={() => downloadCsv(rows, csvName(kind))}
          className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-3.5 py-2 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          הורדת CSV
        </button>
      </div>

      {error ? (
        <div className="rounded-[24px] bg-white p-6 text-sm text-red-600">שגיאה בטעינת התשובות.</div>
      ) : !isLoading && rows.length === 0 ? (
        <div className="rounded-[24px] bg-white p-8 text-center text-sm text-stone-500">
          עדיין אין תשובות. שלח למשתתפים את הקישור לשאלון מהעמוד הראשי של שיר דרך.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r)}
              className="rounded-[20px] bg-white p-5 text-start transition-colors hover:bg-stone-50"
            >
              <p className="font-bold text-stone-900">{r.full_name}</p>
              <p className="mt-1 text-xs text-stone-500">{fmtDateTime(r.created_at)}</p>
              <p className="mt-3 text-sm text-stone-600">
                ממוצע <span className="font-bold text-stone-900 tabular-nums">{fmtAvg(overallAverage(r))}</span>
              </p>
            </button>
          ))}
        </div>
      )}

      <ResponseDialog
        response={selected}
        onClose={() => setSelected(null)}
        onDelete={(r) => {
          if (confirm(`למחוק את השאלון של ${r.full_name}? אין אפשרות לשחזר.`)) del.mutate(r.id);
        }}
        deleting={del.isPending}
      />
    </div>
  );
}

function ResponseDialog({
  response,
  onClose,
  onDelete,
  deleting,
}: {
  response: ShirDerechResponse | null;
  onClose: () => void;
  onDelete: (r: ShirDerechResponse) => void;
  deleting: boolean;
}) {
  return (
    <Dialog open={response !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl" dir="rtl">
        {response && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">{response.full_name}</DialogTitle>
              <p className="text-sm text-stone-500">
                שאלון {KIND_LABEL[response.kind]} · {fmtDateTime(response.created_at)} · ממוצע{" "}
                {fmtAvg(overallAverage(response))}
              </p>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              {DOMAINS.map((d) => (
                <section key={d.id} className="rounded-2xl bg-stone-50 p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-stone-900">{d.title}</h3>
                    <span className="text-sm text-stone-600">
                      ממוצע <b className="tabular-nums">{fmtAvg(domainAverage(response, d))}</b>
                    </span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {d.questions.map((q) => {
                      const v = response.scores[String(q.id)];
                      return (
                        <li key={q.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-stone-700">
                            <span className="text-stone-400">{q.id}. </span>
                            {q.text}
                          </span>
                          <span className="flex items-center gap-2">
                            <ScoreBar value={v} />
                            <b className="w-4 text-center tabular-nums">{v ?? "—"}</b>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              <section className="rounded-2xl bg-stone-50 p-4">
                <h3 className="font-semibold text-stone-900">שאלות פתוחות</h3>
                <div className="mt-3 space-y-4">
                  {OPEN_QUESTIONS[response.kind].map((q) => (
                    <div key={q.id}>
                      <p className="text-sm font-semibold text-violet-700">
                        {q.id}. {q.text}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm text-stone-700">
                        {response.answers[String(q.id)] || "לא נכתבה תשובה"}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => onDelete(response)}
                  disabled={deleting}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-stone-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? "מוחק…" : "מחיקת השאלון"}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScoreBar({ value }: { value: number | undefined }) {
  return (
    <span className="hidden sm:flex gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`h-2 w-4 rounded-sm ${value !== undefined && i <= value ? "bg-violet-500" : "bg-stone-200"}`}
        />
      ))}
    </span>
  );
}
